import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, unref as _unref } from "vue"

const _hoisted_1 = { class: "polyline-cards" }
const _hoisted_2 = { class: "block" }
const _hoisted_3 = { class: "card-title" }

import TrendChart from './TrendChart.vue'
import { getOrderTrend } from '@/api/operation'
import { Ref, ref, reactive } from 'vue'
import { useI18n } from 'vue-i18n'
import { ChartType } from '@/types/enum'
import useI18nTl from '@/hooks/useI18nTl'
import useSyncPolling from '@/hooks/useSyncPolling'
import { StatisticsTimeGrain } from '@/types/types'
import Moment from 'moment'

type ChartData = Array<{
  xData: Array<number>
  yData: Array<number>
}>

enum OrderTrendType {
  Sessions = 'User Sessions',
  Energy = 'Energy Used',
  Fees = 'Fees Collected',
}


import { defineComponent, watch } from 'vue'
import { number } from 'echarts'
const __default__ = defineComponent({
  name: 'OrderTrend',
})

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
  operatorId: {
    type: String,
    required: false,
    default: '0',
  },
},
  setup(__props) {

const props = __props

const POLLING_INTERVAL = 60000



const { t } = useI18n()
const { tl } = useI18nTl('Dashboard')

const chartDataFill = (length: number): ChartData => {
  return Array.from({ length }, () => ({ xData: [], yData: [] }))
}

const isLoading = ref(false)
const legendSelect = ref(OrderTrendType.Sessions)
const chartId = ref('order-trend-polyline')

const orderTrendDatas: Record<string, ChartData> = reactive({
  [OrderTrendType.Sessions]: chartDataFill(1),
  [OrderTrendType.Energy]: chartDataFill(1),
  [OrderTrendType.Fees]: chartDataFill(1),
})

const unitTexts: Record<string, string> = reactive({
  [OrderTrendType.Sessions]: 'Dashboard.sessionsUnit',
  [OrderTrendType.Energy]: 'Dashboard.energyUnit',
  [OrderTrendType.Fees]: 'Dashboard.feesUnit',
})

const timeRange = ref(1)
const timeFormat = ref('')
const beginTime = ref('')
const endTime = ref('')
const grain = ref(StatisticsTimeGrain.STD_DAY)

const timeRangeOptions = [
  { label: tl('last24Hour'), value: 1 },
  { label: tl('yesterday'), value: 2 },
  { label: tl('last30Day'), value: 3 },
  { label: tl('currentMonth'), value: 4 },
  { label: tl('lastMonth'), value: 5 },
  { label: tl('last12Month'), value: 6 },
  { label: tl('currentYear'), value: 7 },
  { label: tl('lastYear'), value: 8 },
]

const parseTimeRange = () => {
  switch (timeRange.value) {
    case 1:
      beginTime.value = Moment().subtract(23, 'hours').format('YYYY-MM-DD HH:mm:ss')
      endTime.value = Moment().endOf('hours').format('YYYY-MM-DD HH:mm:ss')
      grain.value = StatisticsTimeGrain.STD_HOUR
      timeFormat.value = 'MM/DD HH:mm'
      break
    case 2:
      beginTime.value = Moment().subtract(1, 'days').startOf('day').format('YYYY-MM-DD HH:mm:ss')
      endTime.value = Moment().subtract(1, 'days').endOf('day').format('YYYY-MM-DD HH:mm:ss')
      grain.value = StatisticsTimeGrain.STD_HOUR
      timeFormat.value = 'HH:mm'
      break
    case 3:
      beginTime.value = Moment().subtract(30, 'days').format('YYYY-MM-DD HH:mm:ss')
      endTime.value = Moment().format('YYYY-MM-DD HH:mm:ss')
      grain.value = StatisticsTimeGrain.STD_DAY
      timeFormat.value = 'MM/DD'
      break
    case 4:
      beginTime.value = Moment().startOf('month').format('YYYY-MM-DD HH:mm:ss')
      endTime.value = Moment().endOf('month').format('YYYY-MM-DD HH:mm:ss')
      grain.value = StatisticsTimeGrain.STD_DAY
      timeFormat.value = 'MM/DD'
      break
    case 5:
      beginTime.value = Moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD HH:mm:ss')
      endTime.value = Moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD HH:mm:ss')
      grain.value = StatisticsTimeGrain.STD_DAY
      timeFormat.value = 'MM/DD'
      break
    case 6:
      beginTime.value = Moment()
        .subtract(11, 'month')
        .startOf('month')
        .format('YYYY-MM-DD HH:mm:ss')
      endTime.value = Moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD HH:mm:ss')
      grain.value = StatisticsTimeGrain.STD_MONTH
      timeFormat.value = 'YYYY/MM'
      break
    case 7:
      beginTime.value = Moment().startOf('year').format('YYYY-MM-DD HH:mm:ss')
      endTime.value = Moment().endOf('year').format('YYYY-MM-DD HH:mm:ss')
      grain.value = StatisticsTimeGrain.STD_MONTH
      timeFormat.value = 'YYYY/MM'
      break
    case 8:
      beginTime.value = Moment().subtract(1, 'year').startOf('year').format('YYYY-MM-DD HH:mm:ss')
      endTime.value = Moment().subtract(1, 'year').endOf('year').format('YYYY-MM-DD HH:mm:ss')
      grain.value = StatisticsTimeGrain.STD_MONTH
      timeFormat.value = 'YYYY/MM'
      break
  }
}
watch(timeRange, () => {
  parseTimeRange()
  loadChartMetrics()
})

const getLineColors = (index: number) => {
  const totalColors = ['#3D7FF9', '#5D4EFF', '#757789', '#00A890', '#F49845', '#66CFDA']
  // Swap the first and index positions
  const changedColorArr = [...totalColors.splice(0, 1, totalColors[index])]
  totalColors.splice(index, 1, changedColorArr[0])
  return totalColors
}

const toTimestamp = (v: string) => {
  let time = new Date(v)
  return time.getTime()
}

const loadChartMetrics = async () => {
  try {
    isLoading.value = true

    const data = await getOrderTrend({
      beginTime: beginTime.value,
      endTime: endTime.value,
      grain: grain.value,
      operatorID: props.operatorId,
    })

    orderTrendDatas[OrderTrendType.Sessions] = chartDataFill(1)
    orderTrendDatas[OrderTrendType.Energy] = chartDataFill(1)
    orderTrendDatas[OrderTrendType.Fees] = chartDataFill(1)
    const sessionsData = orderTrendDatas[OrderTrendType.Sessions][0]
    const energyData = orderTrendDatas[OrderTrendType.Energy][0]
    const feesData = orderTrendDatas[OrderTrendType.Fees][0]
    data.stats.forEach((stat: any) => {
      sessionsData.xData.push(Number(stat.timestamp) * 1000)
      sessionsData.yData.push(stat.totalOrderNum)
      energyData.xData.push(Number(stat.timestamp) * 1000)
      energyData.yData.push(stat.totalElec)
      feesData.xData.push(Number(stat.timestamp) * 1000)
      feesData.yData.push(stat.totalAmount)
    })
  } catch (error) {
    //
  } finally {
    isLoading.value = false
  }
}
parseTimeRange()
loadChartMetrics()

return (_ctx: any,_cache: any) => {
  const _component_el_radio_button = _resolveComponent("el-radio-button")!
  const _component_el_radio_group = _resolveComponent("el-radio-group")!
  const _component_el_col = _resolveComponent("el-col")!
  const _component_el_option = _resolveComponent("el-option")!
  const _component_el_select = _resolveComponent("el-select")!
  const _component_el_row = _resolveComponent("el-row")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createVNode(_component_el_row, null, {
          default: _withCtx(() => [
            _createVNode(_component_el_col, { span: 20 }, {
              default: _withCtx(() => [
                _createVNode(_component_el_radio_group, {
                  class: "legend-select",
                  modelValue: legendSelect.value,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((legendSelect).value = $event))
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_radio_button, {
                      label: OrderTrendType.Sessions
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_ctx.$t('Dashboard.sessions')), 1)
                      ]),
                      _: 1
                    }, 8, ["label"]),
                    _createVNode(_component_el_radio_button, {
                      label: OrderTrendType.Energy
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_ctx.$t('Dashboard.energy')), 1)
                      ]),
                      _: 1
                    }, 8, ["label"]),
                    _createVNode(_component_el_radio_button, {
                      label: OrderTrendType.Fees
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_ctx.$t('Dashboard.fees')), 1)
                      ]),
                      _: 1
                    }, 8, ["label"])
                  ]),
                  _: 1
                }, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_col, { span: 4 }, {
              default: _withCtx(() => [
                _createVNode(_component_el_select, {
                  class: "interval-select",
                  modelValue: timeRange.value,
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((timeRange).value = $event))
                }, {
                  default: _withCtx(() => [
                    (_openBlock(), _createElementBlock(_Fragment, null, _renderList(timeRangeOptions, (time) => {
                      return _createVNode(_component_el_option, {
                        key: time.label,
                        label: time.label,
                        value: time.value
                      }, null, 8, ["label", "value"])
                    }), 64))
                  ]),
                  _: 1
                }, 8, ["modelValue"])
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _createVNode(TrendChart, {
        "chart-id": chartId.value,
        "chart-data": _unref(orderTrendDatas)[legendSelect.value],
        "y-title": [`test`],
        chartColors: getLineColors(1),
        "unit-text-key": _unref(unitTexts)[legendSelect.value],
        format: timeFormat.value
      }, null, 8, ["chart-id", "chart-data", "chartColors", "unit-text-key", "format"])
    ])
  ]))
}
}

})